<template>
  <v-dialog
    scrollable
    max-width="850px"
    :value="value"
    :fullscreen="isMobile()"
    :persistent="submittingTimePair"
    @click:outside.stop="closeOperation"
    @keydown.esc="closeOperation"
  >
    <v-card elevation="1">
      <v-card-title>
        {{ $t('clock.clockIn') }}
        <v-spacer/>
        <v-btn icon @click="closeOperation">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider/>

      <v-card-text class="py-0 px-2">
        <v-card
          v-for="(subordinate, index) in subordinates"
          width="100%"
          flat
          :key="subordinate.employee.id"
        >
          <v-card-subtitle class="body-1 pb-1">
            {{ subordinate.employee.firstName }} {{ subordinate.employee.lastName }}
          </v-card-subtitle>

          <TimePairConfig
            fullWidth
            :ref="`${clockConfigPrefix}${subordinate.employee.id}`"
            :assignments="subordinate.subordinateAssignments"
            :assignment-text="assignment => assignment.description"
            @update-employee-assignment="selectedAssignments[index].assignment = $event"
            @update-pair-time-type="handleTimePairTimeTypeChange($event, index)"
            @update-time-pair-config="handleConfigChange($event, index)"
          />
          <v-divider v-if="index < subordinates.length - 1"/>
        </v-card>
      </v-card-text>

      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          dark
          :loading="submittingTimePair"
          color="primary"
          @click="submitClockIn"
        >
          {{ $t('clock.clockIn') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';

import isMobile from '@/mixins/displayAdaptable';
import canClockMassShiftIn from '@/mixins/clocking/mass/canClockMassShiftIn';
import attachMobileBackButton from '@/mixins/attachMobileBackButton';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';

import TimePairConfig from '@/components/timepairs/TimePairConfig.vue';
import constants from '@/constants';

export default {
  name: 'ApprovalsClockInDialog',
  components: { TimePairConfig },
  mixins: [attachMobileBackButton, canClockMassShiftIn, hasAppSnackbar, isMobile],

  props: {
    value: Boolean,
    subordinates: Array,
  },

  data() {
    return {
      selectedAssignments: [],
      subordinateConfigurations: [],
      submittingTimePair: false,
      clockConfigPrefix: 'clockInConfig-',
    };
  },

  watch: {
    subordinates() {
      this.resetFields();
    },
  },

  methods: {
    closeOperation() {
      if (this.submittingTimePair) return;

      this.$emit('close');

      this.selectedAssignments = [];
      this.subordinateConfigurations = [];
    },

    resetFields() {
      this.selectedAssignments = [];
      this.subordinateConfigurations = [];

      forEach(this.subordinates, (subordinate) => {
        this.selectedAssignments.push({
          employeeId: subordinate.employee.id,
          assignment: subordinate.subordinateAssignments.length === 1
            ? subordinate.subordinateAssignments[0] : null,
        });

        this.subordinateConfigurations.push({
          employeeId: subordinate.employee.id,
          timePairCustomFieldValues: [],
        });
      });
    },

    handleTimePairTimeTypeChange(value, index) {
      const config = this.subordinateConfigurations[index];
      config.timeType = value;
    },

    handleConfigChange(value, index) {
      const config = this.subordinateConfigurations[index];
      config.timePairCustomFieldValues = value;
    },

    submitClockIn() {
      const { requestDetails, errorDetails } = this.getRequestDetails();

      if (isEmpty(errorDetails)) {
        this.submittingTimePair = true;
        this.createMassShiftClockIn(
          requestDetails,
          (responseData) => this.$emit('clock-in-submitted', responseData),
          () => {
            this.submittingTimePair = false;
            this.closeOperation();
          },
        );
      } else {
        this.showSnackbar({
          text: this.$t('snackbar.completeAllFields'),
          color: constants.AppColors.ERROR,
        });
      }
    },

    getRequestDetails() {
      const requestDetails = [];
      const errorDetails = [];

      forEach(this.subordinates, (subordinate) => {
        const subordinateErrors = this.performClockInFieldValidation(subordinate);
        if (subordinateErrors.length > 0) errorDetails.push(subordinateErrors);
        if (errorDetails.length > 0) return;

        const selectedAssignment = this.getSubordinateSelectedAssignment(subordinate);
        const config = this.getSubordinateConfiguration(subordinate);
        requestDetails.push({
          timePairCustomFieldValues: config.timePairCustomFieldValues,
          employeeAssignmentId: selectedAssignment.id,
          timeZoneOffset: selectedAssignment.timeZoneOffset,
        });
      });

      return { requestDetails, errorDetails };
    },

    performClockInFieldValidation(subordinate) {
      const errorDetails = [];

      const unselectedAssignment = isNull(this.getSubordinateSelectedAssignment(subordinate));
      const isValid = this.$refs[`${this.clockConfigPrefix}${subordinate.employee.id}`][0]
      .$refs
      .form
      .validate();

      if (unselectedAssignment || !isValid > 0) {
        errorDetails.push(`${subordinate.employee.firstName} ${subordinate.employee.lastName}`);
      }

      return errorDetails;
    },

    getSubordinateSelectedAssignment(subordinate) {
      return find(
        this.selectedAssignments,
        (assignment) => assignment.employeeId === subordinate.employee.id,
      ).assignment;
    },

    getSubordinateConfiguration(subordinate) {
      return find(this.subordinateConfigurations,
        (config) => config.employeeId === subordinate.employee.id);
    },
  },
};
</script>

<style scoped>

</style>
